import { useEffect } from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  isSupportedLanguage,
  DEFAULT_LANGUAGE,
  applyDocumentDirection,
  SUPPORTED_LANGUAGES,
  SITE_URL,
  getHreflangCode,
  stripLangPrefix,
  buildLocalizedPath,
} from "../constants/languages";
import NotFoundPage from "../pages/NotFoundPage";

// Mounted for both the unprefixed English branch (no :lang param) and the
// "/:lang" branch. Syncs i18next's active language to the URL, and rejects
// unrecognized language codes (including "/en/..." — English is never
// prefixed, so that path is not considered valid).
const LanguageWrapper = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  const isValid = lang === undefined || (isSupportedLanguage(lang) && lang !== DEFAULT_LANGUAGE);
  const resolvedLang = lang || DEFAULT_LANGUAGE;

  useEffect(() => {
    if (isValid && i18n.language !== resolvedLang) {
      i18n.changeLanguage(resolvedLang);
    }

    if (isValid) {
      applyDocumentDirection(resolvedLang);
    }
  }, [isValid, resolvedLang, lang, i18n]);

  // Canonical + hreflang tags. Both need to agree with each other: canonical
  // must self-reference this exact language's URL (not funnel every language
  // back to English), and hreflang must list every language variant of this
  // same page so Google can match searchers to the right one — e.g. a
  // searcher in Afghanistan should land on the Dari page, not English.
  // Individual pages used to set their own canonical tag hardcoded to the
  // English URL; that's centralized here instead, since this is the one
  // place that already knows both the current path and current language.
  useEffect(() => {
    if (!isValid) return;

    const barePath = stripLangPrefix(location.pathname);

    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", `${SITE_URL}${buildLocalizedPath(barePath, resolvedLang)}`);

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    SUPPORTED_LANGUAGES.forEach(({ code }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", getHreflangCode(code));
      link.setAttribute("href", `${SITE_URL}${buildLocalizedPath(barePath, code)}`);
      document.head.appendChild(link);
    });

    const defaultLink = document.createElement("link");
    defaultLink.setAttribute("rel", "alternate");
    defaultLink.setAttribute("hreflang", "x-default");
    defaultLink.setAttribute("href", `${SITE_URL}${buildLocalizedPath(barePath, DEFAULT_LANGUAGE)}`);
    document.head.appendChild(defaultLink);
  }, [isValid, resolvedLang, location.pathname]);

  if (!isValid) {
    return <NotFoundPage />;
  }

  return <Outlet />;
};

export default LanguageWrapper;
